import { MealTime } from "@/clients/models/MealTime";
import { getTimeOfDayGreeting } from "@/utils/get-time-of-day-greeting";
import { formatMealTime } from "./format-meal-time";

interface BuildMealCoachChatParams {
  /** Meal time the chat is about. Omit/null for a general "add to this day" chat
   *  opened from the FAB or the empty-state pill (no specific slot). */
  mealTime?: MealTime | null;
  /** Plan day key, e.g. "tuesday". */
  day: string;
  /** ISO date (yyyy-MM-dd) for the selected plan day, when available. */
  targetDate?: string | null;
  /** Clerk first name — falls back to a neutral greeting when absent. */
  firstName?: string | null;
  /** Name of the meal currently in the slot, when one is planned. */
  mealName?: string | null;
  /** Backend meal id, included in the agent context when available. */
  mealId?: string | null;
}

export interface MealCoachChat {
  /** NutriCoach greeting seeded as the first (agent) message in the chat. */
  greeting: string;
  /** Natural-language context silently prepended to the user's first reply
   *  so the agent knows which meal time the conversation is about. */
  contextPrefix: string;
}

const capitalise = (value: string): string =>
  value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : value;

/**
 * Builds the contextual chat content for the My Plan meal coach button.
 * The greeting wording mirrors the Figma designs (nodes 1874:16927 /
 * 1974:2991); the variant depends on whether the slot already has a meal.
 */
export const buildMealCoachChat = ({
  mealTime,
  day,
  targetDate,
  firstName,
  mealName,
  mealId,
}: BuildMealCoachChatParams): MealCoachChat => {
  // Warm, "login prompt" lead: "Good evening, Sergio". Drop the name entirely
  // (no "there" placeholder) when we don't have a first name — see the cold-cache
  // name fix in commit 94d3ac76; "there" is a symptom to remove, not a fallback.
  const trimmedName = firstName?.trim();
  const greetingLead = trimmedName
    ? `${getTimeOfDayGreeting()}, ${trimmedName}`
    : getTimeOfDayGreeting();
  // Anchor the weekday to a real calendar date when we have one, so the agent
  // can resolve which week the meal belongs to (e.g. "Tuesday (2026-06-23)").
  const dayLabel = day ? capitalise(day) : "today";
  const dayWithDate = targetDate ? `${dayLabel} (${targetDate})` : dayLabel;

  // General "add to this day" chat — no specific meal time (FAB / empty-state pill).
  if (!mealTime) {
    return {
      greeting: `${greetingLead} — what would you like to add? 💜`,
      contextPrefix:
        `[Context: the user is viewing their ${dayWithDate} plan and wants to add a ` +
        `meal or snack; no specific meal time is selected.]`,
    };
  }

  const mealNoun = formatMealTime(mealTime).toLowerCase();

  if (mealName) {
    return {
      greeting:
        `${greetingLead} 💜\n\n` +
        `I can help you change your **${mealName}** ${mealNoun}, add to it, ` +
        `repeat it later this week — or do something completely different if you want.\n\n` +
        `Just tell me how I can help 🤓`,
      contextPrefix:
        `[Context: the user is viewing their ${dayWithDate} ${mealNoun}, ` +
        `currently planned as "${mealName}"${mealId ? ` (meal id: ${mealId})` : ""}.]`,
    };
  }

  return {
    greeting: `${greetingLead} — what do you fancy for ${mealNoun}? 💜`,
    contextPrefix:
      `[Context: the user is viewing their ${dayWithDate} ${mealNoun}, ` +
      `which has no meal planned yet.]`,
  };
};
